// @ts-check

/** @file Offline task comment create and the atomic comment+state-change intent against an open project replica. Each command writes the local comment row in one replica transaction for immediate feedback, then enqueues a single replay intent on the conflict-tracked SyncClient so the coordinator can replay, rebase, and reconcile it when connectivity returns. */

/** @import {OpenProjectReplica} from "./project-replica.js" */

import Comment from "../models/comment.js"
import Task from "../models/task.js"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "./project-replica.js"
import {syncUuid} from "./project-replica-mutation-log.js"

/**
 * The requested task-state transition attached to a compound offline comment.
 * `baseVersion` is the `updatedAt` the local task row carried when the user
 * authored the intent; it is the optimistic-concurrency guard the server checks
 * so the whole compound command is rejected when the task moved in the meantime.
 * @typedef {object} OfflineTaskStateChange
 * @property {string} state Target business task state.
 * @property {string | null} baseVersion The task `updatedAt` the intent is based on.
 */

/**
 * Reads the current authoritative updatedAt of a local record as an ISO string.
 * @param {{updatedAt: () => Date | string | null}} record Local record.
 * @returns {string | null} The base version, or null when the record has none.
 */
function baseVersionFor(record) {
  const value = record.updatedAt()

  if (value instanceof Date) return value.toISOString()
  if (typeof value === "string" || typeof value === "number") return String(value)

  return null
}

/**
 * Queues an offline standalone comment create. Inserts the local comment row in
 * one transaction for immediate feedback, then enqueues a create-shaped intent
 * (no base version, syncType "update") that the coordinator replays as an
 * append-only Comment create when connectivity returns. The comment UUID is
 * client-supplied so replay is idempotent and restart-safe.
 * @param {object} args Create inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.taskId Task the comment is posted on.
 * @param {string} args.comment Body of the comment.
 * @param {string | null} [args.userId] Author UUID (defaults to null; the server stamps it).
 * @returns {Promise<{comment: InstanceType<typeof Comment>, commentId: string}>} The local comment and its client UUID.
 * @rejects {Error} When the replica is unusable.
 */
export async function queueOfflineCommentCreate({comment, replica, taskId, userId = null}) {
  const commentId = syncUuid()
  const now = new Date().toISOString()

  const localComment = await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    return await operation.forModel(Comment).create({
      comment,
      createdAt: now,
      id: commentId,
      resourceId: taskId,
      resourceType: "Task",
      updatedAt: now,
      userId
    })
  })

  await replica.syncClient.queue({
    data: {comment, resourceId: taskId, resourceType: "Task"},
    operation: "create",
    resource: localComment
  })

  return {comment: localComment, commentId}
}

/**
 * Queues the atomic comment + task-state-change intent. Inserts the local
 * comment row and applies the state change to the local task row in one
 * transaction (so the UI never shows a state change without its comment or
 * vice versa), then enqueues a single create-shaped Comment intent whose
 * payload carries the `taskStateChange` — replayed server-side by the idempotent
 * `createCommentAndChangeTaskState` command that applies both atomically or not
 * at all.
 * @param {object} args Create inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.taskId Task the comment is posted on and whose state changes.
 * @param {string} args.comment Body of the comment.
 * @param {OfflineTaskStateChange} args.taskStateChange The requested state transition.
 * @param {string | null} [args.userId] Author UUID (defaults to null; the server stamps it).
 * @returns {Promise<{comment: InstanceType<typeof Comment>, commentId: string, task: InstanceType<typeof Task>} | null>} The local rows, or null when the task row is absent.
 * @rejects {Error} When the task is not present in the replica.
 */
export async function queueOfflineCommentCreateWithStateChange({comment, replica, taskStateChange, taskId, userId = null}) {
  const commentId = syncUuid()
  const now = new Date().toISOString()

  /** @type {{comment: InstanceType<typeof Comment>, task: InstanceType<typeof Task>} | null} */
  let local = null
  /** The authoritative task updatedAt captured before the optimistic local state change. */
  let baseVersion = null

  await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const task = await operation.forModel(Task).findBy({id: taskId})

    if (!task) throw new Error(`Project replica has no task ${taskId}`)

    // The base version is the authoritative updatedAt the task row carried
    // before this intent; it is the optimistic-concurrency guard the server
    // checks. The optimistic local state change must not clobber it, so the
    // original updatedAt is restored after the save.
    baseVersion = baseVersionFor(task)
    const authoritativeUpdatedAt = task.updatedAt()

    const localComment = await operation.forModel(Comment).create({
      comment,
      createdAt: now,
      id: commentId,
      resourceId: taskId,
      resourceType: "Task",
      updatedAt: now,
      userId
    })

    if (task.state() !== taskStateChange.state) {
      task.setState(taskStateChange.state)
      await task.save()
    }

    if (authoritativeUpdatedAt !== null && authoritativeUpdatedAt !== undefined) {
      task.setUpdatedAt(authoritativeUpdatedAt)
      await task.save()
    }

    local = {comment: localComment, task}
  })

  if (!local) throw new Error(`Project replica has no task ${taskId}`)

  await replica.syncClient.queue({
    data: {
      comment,
      resourceId: taskId,
      resourceType: "Task",
      taskStateChange: {baseVersion, state: taskStateChange.state}
    },
    operation: "create",
    resource: local.comment
  })

  return {comment: local.comment, commentId, task: local.task}
}
